import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getGeneralPrefs,
  setGeneralPrefs,
} from '@/lib/preferences';

interface ThemeContextValue {
  theme: string;
  toggle: () => void;
}

const STORAGE_KEY = 'VudooTrack GPS.theme';
const LEGACY_STORAGE_KEY = 'Kevin GPS .theme';

const ThemeContext =
  createContext<ThemeContextValue>({
    theme: 'light',
    toggle: () => {},
  });

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [theme, setTheme] =
    useState<string>(() => {
      if (
        typeof window ===
        'undefined'
      ) {
        return 'light';
      }

      const savedTheme =
        localStorage.getItem(
          STORAGE_KEY,
        );

      if (savedTheme) {
        return savedTheme;
      }

      const legacyTheme =
        localStorage.getItem(
          LEGACY_STORAGE_KEY,
        );

      if (legacyTheme) {
        localStorage.setItem(
          STORAGE_KEY,
          legacyTheme,
        );

        localStorage.removeItem(
          LEGACY_STORAGE_KEY,
        );

        return legacyTheme;
      }

      return 'light';
    });

  const prefsRestoredRef =
    useRef(false);

  // On mount, load theme from IndexedDB
  // (more durable than localStorage)
  useEffect(() => {
    if (
      prefsRestoredRef.current
    ) {
      return;
    }

    prefsRestoredRef.current =
      true;

    getGeneralPrefs().then(
      (prefs) => {
        if (
          prefs.theme &&
          prefs.theme !== theme
        ) {
          setTheme(
            prefs.theme,
          );

          try {
            localStorage.setItem(
              STORAGE_KEY,
              prefs.theme,
            );

            localStorage.removeItem(
              LEGACY_STORAGE_KEY,
            );
          } catch {
            /* ignore */
          }
        }
      },
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root =
      document.documentElement;

    root.classList.toggle(
      'dark',
      theme === 'dark',
    );

    try {
      localStorage.setItem(
        STORAGE_KEY,
        theme,
      );

      localStorage.removeItem(
        LEGACY_STORAGE_KEY,
      );
    } catch {
      /* ignore */
    }

    // Also persist to IndexedDB for durability
    // (merge with existing prefs)
    getGeneralPrefs().then(
      (existing) => {
        setGeneralPrefs({
          ...existing,
          theme,
        });
      },
    );
  }, [theme]);

  const toggle = () =>
    setTheme((currentTheme) =>
      currentTheme === 'dark'
        ? 'light'
        : 'dark',
    );

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggle,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () =>
  useContext(ThemeContext);
